import { useEffect, useRef, useState, useMemo } from "react";
import maplibregl from "maplibre-gl";
import { LidarControlReact, useLidarState } from "maplibre-gl-lidar/react";
import { createRoot } from "react-dom/client";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import "maplibre-gl-lidar/style.css";
import "maplibre-gl/dist/maplibre-gl.css";
import "../../../css/3dfusion.css";


const Fusion3DModel = ({ annotationMode, annotationText, annotations, onAddAnnotation, onDeleteAnnotation, fileUrl, center = [-122.4, 37.8], zoom = 12 }) => {
  const mapContainer = useRef(null);
  const [map, setMap] = useState(null);
  const { state, setColorScheme, setPointSize, setCollapsed } = useLidarState();
  
  // Memoize center to prevent array recreation
  const memoizedCenter = useMemo(() => center, [center[0], center[1]]);

  useEffect(() => {
    if (!mapContainer.current) return;

    const mapInstance = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {},
        layers: []
      }, // Empty style - no basemap, only point cloud
      center: memoizedCenter,
      zoom: zoom,
      pitch: 60,
      maxPitch: 85, // Allow higher pitch for better 3D viewing
    });

    mapInstance.on("load", () => setMap(mapInstance));

    return () => {
      mapInstance.remove();
    };
  }, [memoizedCenter, zoom]);

  useEffect(() => {
    if (!map) return;
    // Ensure the right-side LidarControl panel is open on initial load
    try {
      setCollapsed(false);
    } catch (e) {
      // ignore if setCollapsed not available
    }
    // Replace the toggle button icon with MUI icon
    const replaceIcon = () => {
      const toggleButton = document.querySelector(
        ".fusion-lidar-map .lidar-control-icon",
      );
      if (toggleButton) {
        toggleButton.innerHTML = "";
        const root = createRoot(toggleButton);
        root.render(<TuneOutlinedIcon sx={{ fontSize: 22 }} />);
      }
    };

    // Delay to ensure the LidarControl is mounted
    setTimeout(replaceIcon, 100);
  }, [map]);

  // Handle map clicks for annotation placement
  useEffect(() => {
    if (!map) return;

    const handleMapClick = (e) => {
      if (annotationMode === "annotate" && annotationText?.trim()) {
        const { lng, lat } = e.lngLat;
        onAddAnnotation?.({ lng, lat });
      }
    };

    map.on("click", handleMapClick);
    return () => map.off("click", handleMapClick);
  }, [map, annotationMode, annotationText, onAddAnnotation]);

  // Update cursor based on annotation mode
  useEffect(() => {
    if (!map) return;
    const canvas = map.getCanvas();
    canvas.style.cursor = annotationMode === "annotate" ? "crosshair" : "";
  }, [map, annotationMode]);

  // Render annotation markers on the map
  useEffect(() => {
    if (!map || !annotations) return;

    const markers = [];
    annotations.features.forEach((feature, index) => {
      const el = document.createElement("div");
      el.className = "annotation-marker-custom";
      el.style.cssText = `
        background-color: #112138;
        color: white;
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 12px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        max-width: 200px;
        z-index: 1000;
        position: relative;
        display: flex;
        align-items: center;
        gap: 8px;
      `;
      
      const textSpan = document.createElement("span");
      textSpan.textContent = feature.properties.text;
      textSpan.style.cssText = `
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
      `;
      
      const deleteBtn = document.createElement("button");
      deleteBtn.innerHTML = "&times;";
      deleteBtn.style.cssText = `
        background: rgba(255, 255, 255, 0.2);
        border: none;
        color: white;
        cursor: pointer;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 16px;
        line-height: 1;
        font-weight: bold;
        transition: background 0.2s;
      `;
      deleteBtn.onmouseover = () => {
        deleteBtn.style.background = "rgba(255, 0, 0, 0.8)";
      };
      deleteBtn.onmouseout = () => {
        deleteBtn.style.background = "rgba(255, 255, 255, 0.2)";
      };
      deleteBtn.onclick = (e) => {
        e.stopPropagation();
        onDeleteAnnotation?.(index);
      };
      
      el.appendChild(textSpan);
      el.appendChild(deleteBtn);

      const marker = new maplibregl.Marker({ 
        element: el,
        anchor: 'bottom'
      })
        .setLngLat(feature.geometry.coordinates)
        .addTo(map);
      
      // Ensure marker container has proper z-index
      const markerElement = marker.getElement();
      if (markerElement && markerElement.parentElement) {
        markerElement.parentElement.style.zIndex = '1000';
      }
      
      markers.push(marker);
    });

    return () => markers.forEach((m) => m.remove());
  }, [map, annotations, onDeleteAnnotation]);

  return (
    <div className="fusion-lidar-map" style={{ height: "100%", width: "100%" }}>
      <div ref={mapContainer} style={{ height: "100%", width: "100%" }} />
      {map && (
        <LidarControlReact
          collapsed={false}
          map={map}
          title={"3D Fusion Model"}
          pointSize={state.pointSize}
          colorScheme={state.colorScheme}
          onLoad={(pc) => {
            console.log("Point Cloud Loaded:", pc);            // Auto-fit map to point cloud bounds
            if (pc && pc.header && pc.header.bounds) {
              const bounds = pc.header.bounds;
              const [minX, minY] = bounds.min;
              const [maxX, maxY] = bounds.max;
              map.fitBounds(
                [[minX, minY], [maxX, maxY]],
                { padding: 50, maxZoom: 18, pitch: 60, duration: 2000 }
              );
            }          }}
          defaultUrl={fileUrl}
        />
      )}
 
    </div>
  );
};

export default Fusion3DModel;
